import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import type {
  AssetsData,
  PropertyAsset,
  SavingsAccount,
  ShareHolding,
  Superannuation,
  Vehicle,
  OtherAsset,
} from "@/types/fact-finder";

interface AssetsSectionProps {
  data: AssetsData | null;
  hasApplicant2: boolean;
  onSave: (data: AssetsData) => Promise<void>;
  onNext: () => void;
}

const defaultData: AssetsData = {
  properties: [],
  savings: [],
  shares: [],
  superannuation: [],
  vehicles: [],
  otherAssets: [],
  wantsInsuranceQuote: false,
};

const defaultProperty: PropertyAsset = {
  address: "",
  suburb: "",
  state: "",
  postcode: "",
  country: "Australia",
  propertyType: "house",
  currentValue: 0,
  datePurchased: "",
  rentalPerWeek: undefined,
  owner: "joint",
  loanBalance: undefined,
  repaymentAmount: undefined,
  lender: "",
};

const defaultSavings: SavingsAccount = {
  accountType: "Savings",
  bankInstitution: "",
  numberOfAccounts: 1,
  amount: 0,
  heldBy: "applicant1",
};

const defaultShares: ShareHolding = {
  heldWith: "",
  numberOfShares: 0,
  value: 0,
  heldBy: "applicant1",
};

const defaultSuper: Superannuation = {
  company: "",
  value: 0,
  heldBy: "applicant1",
};

const defaultVehicle: Vehicle = {
  make: "",
  model: "",
  year: new Date().getFullYear(),
  value: 0,
  heldBy: "applicant1",
};

const defaultOther: OtherAsset = {
  description: "",
  value: 0,
};

export function AssetsSection({ data, hasApplicant2, onSave, onNext }: AssetsSectionProps) {
  const [formData, setFormData] = useState<AssetsData>(data || defaultData);
  const [isSaving, setIsSaving] = useState(false);

  const formatCurrency = (value: number | undefined): string => {
    if (!value) return "";
    return value.toLocaleString();
  };

  const parseCurrency = (value: string): number => {
    return parseInt(value.replace(/,/g, ""), 10) || 0;
  };

  // Property helpers
  const addProperty = () =>
    setFormData((prev) => ({ ...prev, properties: [...prev.properties, { ...defaultProperty }] }));
  const removeProperty = (index: number) =>
    setFormData((prev) => ({ ...prev, properties: prev.properties.filter((_, i) => i !== index) }));
  const updateProperty = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      properties: prev.properties.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    }));

  // Savings helpers
  const addSavings = () =>
    setFormData((prev) => ({ ...prev, savings: [...prev.savings, { ...defaultSavings }] }));
  const removeSavings = (index: number) =>
    setFormData((prev) => ({ ...prev, savings: prev.savings.filter((_, i) => i !== index) }));
  const updateSavings = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      savings: prev.savings.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));

  // Shares helpers
  const addShares = () =>
    setFormData((prev) => ({ ...prev, shares: [...prev.shares, { ...defaultShares }] }));
  const removeShares = (index: number) =>
    setFormData((prev) => ({ ...prev, shares: prev.shares.filter((_, i) => i !== index) }));
  const updateShares = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      shares: prev.shares.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));

  // Super helpers
  const addSuper = () =>
    setFormData((prev) => ({ ...prev, superannuation: [...prev.superannuation, { ...defaultSuper }] }));
  const removeSuper = (index: number) =>
    setFormData((prev) => ({ ...prev, superannuation: prev.superannuation.filter((_, i) => i !== index) }));
  const updateSuper = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      superannuation: prev.superannuation.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));

  // Vehicle helpers
  const addVehicle = () =>
    setFormData((prev) => ({ ...prev, vehicles: [...prev.vehicles, { ...defaultVehicle }] }));
  const removeVehicle = (index: number) =>
    setFormData((prev) => ({ ...prev, vehicles: prev.vehicles.filter((_, i) => i !== index) }));
  const updateVehicle = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      vehicles: prev.vehicles.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    }));

  // Other helpers
  const addOther = () =>
    setFormData((prev) => ({ ...prev, otherAssets: [...prev.otherAssets, { ...defaultOther }] }));
  const removeOther = (index: number) =>
    setFormData((prev) => ({ ...prev, otherAssets: prev.otherAssets.filter((_, i) => i !== index) }));
  const updateOther = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      otherAssets: prev.otherAssets.map((o, i) => (i === index ? { ...o, [field]: value } : o)),
    }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  const ownerOptions = hasApplicant2
    ? [
        { value: "applicant1", label: "Applicant 1" },
        { value: "applicant2", label: "Applicant 2" },
        { value: "joint", label: "Joint" },
      ]
    : [{ value: "applicant1", label: "Applicant 1" }];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">Assets</h2>
        <p className="text-muted-foreground mt-1">
          List all your assets including properties, savings, investments, and vehicles.
        </p>
      </div>

      {/* Properties */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Properties</h3>
          <Button variant="outline" onClick={addProperty} className="gap-2">
            <FiPlus className="size-4" />
            Add Property
          </Button>
        </div>

        {formData.properties.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No properties added. Click "Add Property" to add real estate assets.
          </p>
        ) : (
          formData.properties.map((property, idx) => (
            <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Property {idx + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeProperty(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <FiTrash2 className="size-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Address</label>
                <Input
                  value={property.address}
                  onChange={(e) => updateProperty(idx, "address", e.target.value)}
                  placeholder="Street address"
                />
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Suburb</label>
                  <Input
                    value={property.suburb}
                    onChange={(e) => updateProperty(idx, "suburb", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">State</label>
                  <Select
                    value={property.state}
                    onValueChange={(v) => updateProperty(idx, "state", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Postcode</label>
                  <Input
                    value={property.postcode}
                    onChange={(e) => updateProperty(idx, "postcode", e.target.value)}
                    maxLength={4}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Property Type</label>
                  <Select
                    value={property.propertyType}
                    onValueChange={(v) => updateProperty(idx, "propertyType", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="townhouse">Townhouse</SelectItem>
                      <SelectItem value="unit">Unit/Apartment</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Current Value ($)</label>
                  <Input
                    value={formatCurrency(property.currentValue)}
                    onChange={(e) => updateProperty(idx, "currentValue", parseCurrency(e.target.value))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Date Purchased</label>
                  <Input
                    type="date"
                    value={property.datePurchased}
                    onChange={(e) => updateProperty(idx, "datePurchased", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Rental ($/week)</label>
                  <Input
                    type="number"
                    value={property.rentalPerWeek || ""}
                    onChange={(e) => updateProperty(idx, "rentalPerWeek", parseInt(e.target.value) || undefined)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Owner</label>
                  <Select
                    value={property.owner}
                    onValueChange={(v) => updateProperty(idx, "owner", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ownerOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                      <SelectItem value="trust_other">Trust/Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Loan Balance ($)</label>
                  <Input
                    value={formatCurrency(property.loanBalance)}
                    onChange={(e) => updateProperty(idx, "loanBalance", parseCurrency(e.target.value) || undefined)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Repayment Amount ($)</label>
                  <Input
                    value={formatCurrency(property.repaymentAmount)}
                    onChange={(e) => updateProperty(idx, "repaymentAmount", parseCurrency(e.target.value) || undefined)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Lender</label>
                  <Input
                    value={property.lender}
                    onChange={(e) => updateProperty(idx, "lender", e.target.value)}
                    placeholder="e.g. CBA, Westpac"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Savings */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Savings & Bank Accounts</h3>
          <Button variant="outline" onClick={addSavings} className="gap-2">
            <FiPlus className="size-4" />
            Add Account
          </Button>
        </div>

        {formData.savings.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No accounts added. Click "Add Account" to add savings or bank accounts.
          </p>
        ) : (
          formData.savings.map((account, idx) => (
            <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Account {idx + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSavings(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <FiTrash2 className="size-4" />
                </Button>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Account Type</label>
                  <Input
                    value={account.accountType}
                    onChange={(e) => updateSavings(idx, "accountType", e.target.value)}
                    placeholder="e.g. Savings, Term Deposit"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Bank/Institution</label>
                  <Input
                    value={account.bankInstitution}
                    onChange={(e) => updateSavings(idx, "bankInstitution", e.target.value)}
                    placeholder="e.g. CBA"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Amount ($)</label>
                  <Input
                    value={formatCurrency(account.amount)}
                    onChange={(e) => updateSavings(idx, "amount", parseCurrency(e.target.value))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Held By</label>
                  <Select
                    value={account.heldBy}
                    onValueChange={(v) => updateSavings(idx, "heldBy", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ownerOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Shares */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Shares & Investments</h3>
          <Button variant="outline" onClick={addShares} className="gap-2">
            <FiPlus className="size-4" />
            Add Shares
          </Button>
        </div>

        {formData.shares.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No shares added. Click "Add Shares" to add share holdings.
          </p>
        ) : (
          formData.shares.map((share, idx) => (
            <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Shares {idx + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeShares(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <FiTrash2 className="size-4" />
                </Button>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Held With</label>
                  <Input
                    value={share.heldWith}
                    onChange={(e) => updateShares(idx, "heldWith", e.target.value)}
                    placeholder="e.g. CommSec, SelfWealth"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Number of Shares</label>
                  <Input
                    type="number"
                    value={share.numberOfShares || ""}
                    onChange={(e) => updateShares(idx, "numberOfShares", parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Value ($)</label>
                  <Input
                    value={formatCurrency(share.value)}
                    onChange={(e) => updateShares(idx, "value", parseCurrency(e.target.value))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Held By</label>
                  <Select
                    value={share.heldBy}
                    onValueChange={(v) => updateShares(idx, "heldBy", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ownerOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Superannuation */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Superannuation</h3>
          <Button variant="outline" onClick={addSuper} className="gap-2">
            <FiPlus className="size-4" />
            Add Super
          </Button>
        </div>

        {formData.superannuation.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No superannuation added. Click "Add Super" to add super accounts.
          </p>
        ) : (
          formData.superannuation.map((sup, idx) => (
            <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Super {idx + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSuper(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <FiTrash2 className="size-4" />
                </Button>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Company/Fund</label>
                  <Input
                    value={sup.company}
                    onChange={(e) => updateSuper(idx, "company", e.target.value)}
                    placeholder="e.g. AustralianSuper"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Value ($)</label>
                  <Input
                    value={formatCurrency(sup.value)}
                    onChange={(e) => updateSuper(idx, "value", parseCurrency(e.target.value))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Held By</label>
                  <Select
                    value={sup.heldBy}
                    onValueChange={(v) => updateSuper(idx, "heldBy", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="applicant1">Applicant 1</SelectItem>
                      {hasApplicant2 && <SelectItem value="applicant2">Applicant 2</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Vehicles */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Vehicles</h3>
          <Button variant="outline" onClick={addVehicle} className="gap-2">
            <FiPlus className="size-4" />
            Add Vehicle
          </Button>
        </div>

        {formData.vehicles.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No vehicles added. Click "Add Vehicle" to add motor vehicles.
          </p>
        ) : (
          formData.vehicles.map((vehicle, idx) => (
            <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Vehicle {idx + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeVehicle(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <FiTrash2 className="size-4" />
                </Button>
              </div>

              <div className="grid md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Make</label>
                  <Input
                    value={vehicle.make}
                    onChange={(e) => updateVehicle(idx, "make", e.target.value)}
                    placeholder="e.g. Toyota"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Model</label>
                  <Input
                    value={vehicle.model}
                    onChange={(e) => updateVehicle(idx, "model", e.target.value)}
                    placeholder="e.g. Camry"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Year</label>
                  <Input
                    type="number"
                    value={vehicle.year}
                    onChange={(e) => updateVehicle(idx, "year", parseInt(e.target.value) || 2024)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Value ($)</label>
                  <Input
                    value={formatCurrency(vehicle.value)}
                    onChange={(e) => updateVehicle(idx, "value", parseCurrency(e.target.value))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Held By</label>
                  <Select
                    value={vehicle.heldBy}
                    onValueChange={(v) => updateVehicle(idx, "heldBy", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ownerOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Other Assets */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Other Assets</h3>
          <Button variant="outline" onClick={addOther} className="gap-2">
            <FiPlus className="size-4" />
            Add Other
          </Button>
        </div>

        {formData.otherAssets.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No other assets added. Click "Add Other" to add boats, trailers, etc.
          </p>
        ) : (
          formData.otherAssets.map((other, idx) => (
            <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Other Asset {idx + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOther(idx)}
                  className="text-destructive hover:text-destructive"
                >
                  <FiTrash2 className="size-4" />
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Input
                    value={other.description}
                    onChange={(e) => updateOther(idx, "description", e.target.value)}
                    placeholder="e.g. Boat, Caravan, Art collection"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Value ($)</label>
                  <Input
                    value={formatCurrency(other.value)}
                    onChange={(e) => updateOther(idx, "value", parseCurrency(e.target.value))}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Insurance */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Home & Contents Insurance</h3>
        <div className="p-4 bg-muted/30 rounded-lg space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Insurance Company</label>
              <Input
                value={formData.homeContentsInsurance?.company || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    homeContentsInsurance: {
                      company: e.target.value,
                      insuredValue: prev.homeContentsInsurance?.insuredValue || 0,
                    },
                  }))
                }
                placeholder="e.g. AAMI, NRMA"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Insured Value ($)</label>
              <Input
                value={formatCurrency(formData.homeContentsInsurance?.insuredValue)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    homeContentsInsurance: {
                      company: prev.homeContentsInsurance?.company || "",
                      insuredValue: parseCurrency(e.target.value),
                    },
                  }))
                }
                placeholder="0"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="insuranceQuote"
              checked={formData.wantsInsuranceQuote}
              onChange={(e) => setFormData((prev) => ({ ...prev, wantsInsuranceQuote: e.target.checked }))}
              className="w-5 h-5 rounded border-gray-300"
            />
            <label htmlFor="insuranceQuote" className="text-sm">
              I would like an insurance quote
            </label>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? "Saving..." : "Save & Continue"}
        </Button>
      </div>
    </div>
  );
}
